import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import { deriveScaleLabel } from "./types";
import type { UnitDetails, UnitAssignment } from "./types";

interface CurrentAssignmentBadgeProps {
  assignment: UnitAssignment;
  /** Catalog details for the assigned URI. `null` during an in-flight lookup
   *  OR when the URI is genuinely missing — `isMissing` disambiguates. */
  details: UnitDetails | null;
  /** Explicit "URI is not in the live catalog" signal (server 404). Set only
   *  by the lookup hook on a definitive miss, NOT while loading or on
   *  transient errors — otherwise the warning would flash on every reopen
   *  during the in-flight fetch. */
  isMissing: boolean;
  /** Whether × stages a clear. Read-only mode (importer) hides ×. */
  canClear: boolean;
  /** True when the user has clicked × — badge dims, footer reflects the
   *  staged clear. */
  isStagedForClear: boolean;
  /** Click × → request a staged clear. */
  onStageClear: () => void;
  /** Used by the catalog-miss variant to focus the search input. */
  onPickReplacement?: () => void;
}

/**
 * Renders the column's current unit assignment as a pill at the top of the
 * picker in edit mode. Two visual variants:
 *
 * - `variant=assigned` (live catalog entry): accent border, full label +
 *   symbol + scale. × stages a clear.
 * - `variant=missing` (catalog miss): warning border, raw URI shown, inline
 *   "Pick replacement" focuses the search.
 */
export function CurrentAssignmentBadge({
  assignment,
  details,
  isMissing,
  canClear,
  isStagedForClear,
  onStageClear,
  onPickReplacement,
}: CurrentAssignmentBadgeProps) {
  const accent = isMissing ? "var(--color-warning)" : "var(--color-accent)";
  const accentBg = isMissing
    ? "var(--color-warning-bg)"
    : "var(--color-accent-subtle)";
  const accentFg = isMissing ? "var(--color-warning)" : "var(--color-accent)";
  // Prefer the live catalog row's label, fall back to the wire-egress-
  // resolved label on the assignment itself, finally to the raw URI. The
  // assignment-level fields are populated by the server's unit_catalog
  // resolver on snapshot egress — so even while the lookup is in flight
  // the badge shows the real name instead of "unit:RAD-PER-SEC".
  const labelText =
    details?.label ?? assignment.label ?? assignment.stored_unit_uri;
  const symbolText = details?.symbol ?? assignment.symbol ?? null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing-2xs)",
        padding: "var(--spacing-sm) var(--spacing-md)",
        background: accentBg,
        border: `var(--size-border-hairline) solid ${accent}`,
        borderRadius: "var(--radius-app)",
        color: accentFg,
        opacity: isStagedForClear ? 0.5 : 1,
        transition: "opacity var(--duration-micro)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-xs)",
        }}
      >
        <strong style={{ fontWeight: 600, fontSize: "var(--font-size)" }}>
          {labelText}
        </strong>
        {symbolText && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--font-size-small)",
              color: "var(--color-fg-muted)",
            }}
          >
            {symbolText}
          </span>
        )}
        {(() => {
          const short = deriveScaleLabel(assignment.scale_exponent);
          const text =
            short ??
            (assignment.scale_exponent !== 0
              ? `10^${assignment.scale_exponent}`
              : null);
          return text ? (
            <span style={{ fontSize: "var(--font-size-small)" }}>· {text}</span>
          ) : null;
        })()}
        {canClear && (
          <button
            type="button"
            aria-label="Clear assignment"
            onClick={onStageClear}
            style={{
              marginInlineStart: "auto",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "inherit",
              opacity: 0.7,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <CloseIcon style={{ fontSize: "var(--font-size-small)" }} />
          </button>
        )}
      </div>
      {isMissing && (
        <div
          style={{
            fontSize: "var(--font-size-x-small)",
            color: "var(--color-fg-muted)",
          }}
        >
          No longer in catalog —{" "}
          <button
            type="button"
            onClick={onPickReplacement}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              color: accentFg,
              cursor: "pointer",
              textDecoration: "underline",
              fontFamily: "inherit",
              fontSize: "inherit",
            }}
          >
            pick replacement
          </button>
        </div>
      )}
      {isStagedForClear && (
        <div style={{ fontSize: "var(--font-size-x-small)" }}>
          Will be cleared on Apply
        </div>
      )}
    </div>
  );
}
